// Fixed-position flyout coordinates for panels that must escape the sidebar's `overflow: hidden`
// (desktop rail). Extracted from the Phase 22 SidebarGroup so the workflow switcher and any future
// rail flyout share one implementation.
import { useCallback, useRef, useState } from 'react';

/** Below this width the rail is a full-width drawer and flyouts render inline instead. */
export const RAIL_BREAKPOINT = 992;

export interface FlyoutPosition {
  top: number;
  left: number;
}

export interface UseFlyoutPositionResult<T extends HTMLElement> {
  /** Attach to the element the flyout should align with vertically. */
  anchorRef: React.RefObject<T | null>;
  /** `position: fixed` coords, or `null` before the first measure. */
  position: FlyoutPosition | null;
  /** Re-measure from the anchor's current box. Call on open and on viewport changes. */
  measure: () => void;
  /** True when the viewport is wide enough for the fixed rail (and so for fixed flyouts). */
  isRail: () => boolean;
}

export function useFlyoutPosition<T extends HTMLElement>(): UseFlyoutPositionResult<T> {
  const anchorRef = useRef<T | null>(null);
  const [position, setPosition] = useState<FlyoutPosition | null>(null);

  const isRail = useCallback(
    () => typeof window !== 'undefined' && window.innerWidth >= RAIL_BREAKPOINT,
    [],
  );

  const measure = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Horizontally the flyout starts where the SIDEBAR ends, not where the anchor ends — the anchor
    // is inset from the rail edge, so measuring off it would leave the panel floating over the rail.
    // Flush, with no gap and no overlap: the pointer crosses straight from rail to panel.
    const rail = anchorRef.current?.closest('.sidebar')?.getBoundingClientRect();
    setPosition({ top: rect.top, left: rail ? rail.right : rect.right });
  }, []);

  return { anchorRef, position, measure, isRail };
}
